import React, { Fragment, useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { useNavigate } from 'react-router-dom';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { ENUM_PROCUREMENT_STATUS } from 'models/procurement.model';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import useExecutiveContext from 'contexts/executive.context/context';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import LocalModal from 'components/base/local-modal';

const ExecutiveUpdateProcurement = () => {
  const { procurementToUpdate } = useExecutiveContext();
  const [isUpdatingProcurement, setIsUpdatingProcurement] = useState(false);
  const [addNoteModalActive, setAddNoteModalActive] = useState(false);
  const [note, setNote] = useState('');
  const router = useNavigate();
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to procurement');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);
  const editableStatus = [ENUM_PROCUREMENT_STATUS.REQUESTED];
  const is_editable = editableStatus.includes(procurementToUpdate.status);

  const approveProcurementRequest = async () => {
    try {
      setIsUpdatingProcurement(true);
      const response = await apiService.put(`procurement/${procurementToUpdate.id}/status`, {
        data: { status: ENUM_PROCUREMENT_STATUS.APPROVED, note: '' },
        version: 'v2'
      });
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.EXECUTIVE_PROCUREMENTS);
        toast(<ToastMessage message='Procurement request approved successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error approving procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurement(false);
    }
  };

  const rejectProcurementRequest = async () => {
    try {
      setIsUpdatingProcurement(true);
      const response = await apiService.put(`procurement/${procurementToUpdate.id}/status`, {
        data: { status: ENUM_PROCUREMENT_STATUS.CANCELLED, note },
        version: 'v2'
      });
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.EXECUTIVE_PROCUREMENTS);
        toast(<ToastMessage message='Procurement request rejected' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error rejejcting procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurement(false);
    }
  };

  return (
    <div className='ml-2 md:ml-8'>
      <LocalModal isOpen={addNoteModalActive} onRequestClose={() => setAddNoteModalActive(false)}>
        <div className='w-full'>
          <Input
            type='text'
            label='Add a note'
            placeholder='Why are you rejecting this procurement request'
            name='note'
            className='w-full'
            inputClass='!border-b-[2px] !border-b-[#D5D6D7] bg-[#F4F5F5] !rounded-none h-[94px]'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div>
          <hr className='border-t-4 border-[#F7F7F8]' />
          <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
            <Button variant='link' color='gray' small onClick={() => router(-1)}>
              Cancel
            </Button>
            <Button
              variant='block'
              color='primary'
              small
              className='w-[153px] h-[43px]'
              onClick={() => rejectProcurementRequest()}
              loading={isUpdatingProcurement}>
              Reject
            </Button>
          </div>
        </div>
      </LocalModal>
      <div className='flex items-center'>
        <h4 className='text-[24px] font-semibold text-[#2E3031] mr-2'>
          Request #{procurementToUpdate.code}
        </h4>
        <ProcurementStatusTag type={procurementToUpdate.status} />
      </div>
      <p className='text-[#6F7376] text-sm mb-8'>
        {procurementToUpdate.status === ENUM_PROCUREMENT_STATUS.REJECTED
          ? 'Rejected procurement request'
          : procurementToUpdate?.status === ENUM_PROCUREMENT_STATUS.APPROVED
          ? 'Approved procurement request'
          : 'Approve or reject main store procurement request'}
      </p>
      <div className='border-4 border-[#F7F7F8] rounded py-[24px]  md:max-w-[670px]'>
        <div className='flex justify-between px-[28px]'>
          <div className='pb-2'>
            <p className='font-semibold text-[20px]'>
              {procurementToUpdate.status === ENUM_PROCUREMENT_STATUS.REJECTED
                ? 'Rejected request'
                : procurementToUpdate?.status === ENUM_PROCUREMENT_STATUS.APPROVED
                ? 'Approved request'
                : 'Update request'}
            </p>
            <p className='text-[#6F7376] text-sm'>
              {procurementToUpdate.status === ENUM_PROCUREMENT_STATUS.REJECTED
                ? 'Rejected procurement request'
                : procurementToUpdate?.status === ENUM_PROCUREMENT_STATUS.APPROVED
                ? 'Approved procurement request'
                : 'Approve or reject main store procurement request'}
            </p>
          </div>
        </div>
        {procurementToUpdate.items.map((item, index) => (
          <div
            className='flex justify-between py-[24px] px-[28px] border-b border-b-[#F7F7F8]'
            key={index}>
            <div className='flex gap-4 items-center'>
              <div>
                <p className='font-medium text-[#2E3031] capitalize'>{item?.ingredient?.name}</p>
                <div className='flex gap-2 flex-wrap'>
                  <p className='text-sm font-medium text-[#2E3031] capitalize'>
                    {item.quantity} {item?.ingredient?.unit_type}
                  </p>

                  <div className='flex items-center gap-1'>
                    <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                    <p className='text-[#6F7376] text-sm font-medium'>{item?.vendor?.name}</p>
                    <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                    <p className='text-[#6F7376] text-sm font-medium'>
                      ₦ {Number(item.price).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex items-center gap-6'>
              <ProcurementStatusTag type={item.status} />
            </div>
          </div>
        ))}
        <div className='flex justify-end gap-4 mt-6 items-center mb-[40px] pr-[28px]'>
          <p className='text-sm text-[#6F7376]'>Total</p>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>
            ₦ {Number(procurementToUpdate.expected_cost).toLocaleString()}
          </h4>
        </div>
        <div>
          <hr className='border-t-4 border-[#F7F7F8]' />
          <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
            {is_editable ? (
              <Fragment>
                <Button
                  variant='block'
                  color='primary'
                  small
                  className='w-[153px] h-[43px]'
                  onClick={() => approveProcurementRequest()}
                  loading={isUpdatingProcurement}>
                  Approve
                </Button>
                <Button
                  variant='block'
                  color='light-gray'
                  small
                  className='w-[153px] h-[43px]'
                  onClick={() => setAddNoteModalActive(true)}
                  loading={isUpdatingProcurement}>
                  Reject
                </Button>
              </Fragment>
            ) : (
              <Button variant='link' color='primary' small onClick={() => router(-1)}>
                Back
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveUpdateProcurement;
